import { Pagination } from 'interfaces/notifications';
import { StringToBoolMap } from 'interfaces';

export enum Routes {
  AUDIENCE_MANAGER = `/main/audience-manager`,
  CUSTOM_AUDIENCE_MANAGER = `/main/custom-audience-manager`,
  AUDIENCE_STUDIO = '/main/audience-studio',
  AUDIENCE_STUDIO_EDITOR = '/main/audience-studio/editor',
  AUDIENCE_STUDIO_AUDIENCE_SELECTOR = '/main/audience-studio/audienceType',
  AUDIENCE_STUDIO_COMBINE_AUDIENCE = '/main/audience-studio/combineAudience',
  OAUTH_URLS = '/main/oauth-urls',
  SEGMENT_MANAGER = '/main/segment-manager',
  SEGMENT_MANAGER_DETAIL = '/main/segment-manager/:platform/:segmentId',
  SEGMENT_MANAGER_DETAIL_REMODELING = '/main/segment-manager/:platform/:segmentId/remodeling',
  LOGIN = '/login',
}

export enum ENUM_REGEX {
  DECIMAL = '^\\d*\\.?\\d*$',
  WHOLE_NUMBER = '^\\d+$',
  POSITIVE_WHOLE_NUMBER = '^[1-9]\\d*$',
  TWO_DECIMAL_PLACES = '^\\d*\\.?\\d{0,2}$',
  NEXXEN_WHOLE_OR_MINUS_ONE = '^(-1|\\d+)$',
}

/** Webpack sets `process.env.KDS_NODE_ENV` via `--env env=…`. */
export const isKdsNonProductionBuild = (): boolean =>
  process.env.KDS_NODE_ENV !== 'production';

export enum UploadStates {
  DONE = 'DONE',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
}
export enum NotificationStates {
  DONE = 'DONE',
  ERROR = 'ERROR',
}

export enum LoadingStates {
  IDLE = 'idle',
  LOADING = 'loading',
  DONE = 'done',
  FAILED = 'failed',
  ERROR = 'error',
  WARNING = 'warning',
}

export enum KDSPageNames {
  AUDIENCESTUDIO = 'audience-studio',
  CUSTOMAUDIENCES = 'custom-audience-manager',
  OAUTH_URLS = 'oauth-urls',
  SEGMENT_MANAGER = 'segment-manager',
}

// Segment Manager Constants
export const SEGMENT_MANAGER_DEFAULT_PLATFORM = 'TTD';
export const SEGMENT_MANAGER_DEFAULT_SORT_BY = 'lastUpdatedAt';
export const SEGMENT_MANAGER_DEFAULT_SORT_ORDER = 'DESC';
export const SEGMENT_MANAGER_DEFAULT_PAGE = 1;
export const SEGMENT_MANAGER_DEFAULT_RECORD_PER_PAGE = 25;
export const SEGMENT_MANAGER_ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// Segment Detail Tab Keys
export enum SegmentDetailTabs {
  OVERVIEW = 'overview',
  REMODELING = 'remodeling',
}

// Rate Level Enum
export enum RateLevel {
  SYSTEM = 'System',
  ADVERTISER = 'Advertiser',
  PARTNER = 'Partner',
}

export const VALID_RATE_LEVELS = Object.values(RateLevel);
export const SEGMENT_MANAGER_DEFAULT_RATE_LEVEL = RateLevel.SYSTEM;

export const SEGMENT_MANAGER_COLUMN_KEYS = {
  TTD: {
    SELECT: 'select',
    SEGMENT_NAME: 'segmentName',
    RECEIVED_IDS: 'receivedIds',
    ACTIVE_IDS: 'activeIds',
    CPM: 'cpm',
    POM: 'pom',
    IMPRESSIONS: 'impressions',
    EFFECTIVE_CPM: 'effectiveCpm',
    REVENUE: 'revenue',
    LAST_UPDATED_AT: 'lastUpdatedAt',
  },
};

export const SEGMENT_MANAGER_GRAPHQL_ENDPOINTS = {
  TTD: 'ttdOtsSegments',
};

export enum ValidationErrorMessages {
  PAYOUT = 'Payout value must be zero or a positive number',
  POINTS = 'Klover Points value must be zero or a positive number',
  CONVERSION_CAPPING = 'Conversion Capping value must be zero or a positive number',
  TUNE_GOALS_POINTS_AWARDED = 'Klover Points Award for Tune Goal must be zero or a positive number',
  TUNE_GOALS_ID = 'Tune Goal ID for Tune Goal must be zero or a positive number',
  TUNE_GOALS_ID_REQUIRED = 'Tune Goal ID for Tune Goal is required',
  TUNE_GOALS_COMPLETION_ORDER_REQUIRED = 'Goal Completion Order for Tune Goal is required',
  TUNE_GOALS_COMPLETION_ORDER_IS_NOT_A_NUMBER = 'Goal Completion Order for Tune Goal must be zero or a positive number',
  TUNE_GOALS_COMPLETION_ORDER_IS_GREATER = 'Goal Completion Order for Tune Goal should be less than the total number of goals',
  TUNE_GOALS_CONVERSION_VALUE = 'Cap Limit for Tune Goal must be zero or a positive number',
  NON_NEGATIVE_NUMBER = 'Value must be zero or a positive number',
  FUTURE_START_DATE_ACTIVE_OFFER = 'Future start date not allowed for active offer',
  START_DATE_SHOULD_BE_IN_FUTURE = 'Date should be in future for inactive offer',
  END_DATE_EARLIER_THAN_START_DATE = 'End Date should not be before start date',
  CURRENT_DATE_NOT_IN_RANGE = 'Ensure current date falls within flight date range',
  CONSECUTIVE_GREATER_THAN_SYMBOL_ERROR = "Invalid segment name: Consecutive '>>' symbols are not permitted.",
  FORBIDDEN_CHARACTER_ERROR = `Do not include special characters such as ', ", or ^`,
  EXPERIAN_FORBIDDEN_CHARACTER_ERROR = `Do not include non-ASCII characters such as double quote (“), backslash (\\), double backslash (\\\\), double slash (//)`,
  EXPERIAN_DESCRIPTION_NON_ASCII_CHARACTER_ERROR = `Do not include non-ASCII characters such as double quote (“), backslash (\\), double backslash (\\\\), double slash (//)`,
  SEGMENT_SECTION_TOO_LONG_ERROR = 'Segment Child name should not exceed 256 characters',
  EXPERIAN_SEGMENT_SECTION_TOO_LONG_ERROR = 'Segment Child name should not exceed 100 characters',
  SEGMENT_SECTION_SHOULD_NOT_EXCEED_ERROR = 'Segment name should not exceed ${MAX_SEGMENT_NAME_LENGTH} characters',
  SEGMENT_NAME_START_CHAR_ERROR = `Segment name must start with "Attain >"`,
  TTD_DATA_ALLIANCE_SEGMENT_NAME_START_CHAR_ERROR = `Segment name must start with "Data Alliance >"`,
  SEGMENT_NAME_A_PATTERN_ERROR = `Segment name must contain "A-" followed by 7 digits (e.g., A-1234567)`,
  META_SEED_EXPORT_MINIMUM_USERS_ERROR = 'Export Error: Your ADS seed audience must contain over {{MIN_USER_COUNT}} individuals to perform Meta Seed Export',
  TIKTOK_SEED_EXPORT_MINIMUM_USERS_ERROR = 'Export Error: Your ADS seed audience must contain at least {{MIN_USER_COUNT}} individuals to perform TikTok Seed Export',
  DESCRIPTION_SECTION_TOO_LONG_ERROR = 'Description should not exceed 256 characters',
  EXPERIAN_DESCRIPTION_TOO_LONG_ERROR = 'Description should not exceed 255 characters',
  DESCRIPTION_REQUIRED_ERROR = 'Description is required and cannot be empty',
  AMAZON_DESCRIPTION_LENGTH_ERROR = 'Description must not exceed 1000 characters',
  AMAZON_DESCRIPTION_ALPHANUMERIC_ERROR = 'Description must be alphanumeric',
  GOAL_COMPLETION_INSTRUCTION_ERROR = 'Goal Completion Instructions is required incase of multiple goals',
  POINTS_AWARDED_REQUIRED = 'Klover Points Award for Tune Goal is required',
  INVALID_CTA_URL = 'Clickthrough Url must have param offer_id with value',
  CTA_URL_REQUIRED = 'Clickthrough Url is required',
  NEEDS_A_LABEL = 'Needs a label',
  URL_REQUIRED = '${field} is required',
  URL_INVALID = '${field} is not valid',
  URL_PROTOCOL_INVALID = '${field} must start with http:// or https://',
  URL_MISSING_PARAMS = '${field} must include the parameter(s) with value: ${params}',
  GOAL_POINT_CHANGE_REQUIRED_VALID_DETAILS = 'Goal Points Change required valid data',
  POINT_CHANGE_REQUIRED_VALID_DETAILS = 'Points Change required valid data',
  POINT_CHANGE_TYPE_REQUIREDS = 'Points Change Type is required',
  YAHOO_DSP_CPM_RANGE_ERROR = 'CPM must be between 0 and 50',
  YAHOO_DSP_TV_CPM_RANGE_ERROR = 'TV CPM must be between 0 and 50',
  YAHOO_DSP_SEGMENT_NAME_LENGTH_ERROR = 'Segment name must not exceed 255 characters',
  YAHOO_DSP_CUSTOM_SEGMENT_PREFIX = 'Segment name must start with "Custom > " for Yahoo Custom segments.',
  YAHOO_DSP_OTS_SEGMENT_PREFIX = 'Segment name must start with "Syndicated > " for Yahoo OTS segments.',
  LIVERAMP_SEGMENT_NAME_LENGTH_ERROR = 'Segment name must not exceed 255 characters',
  YAHOO_DSP_PARTNER_ID_REQUIRED_ERROR = 'Partner ID is required',
  TIKTOK_SEGMENT_NAME_LENGTH_ERROR = 'Segment name must not exceed 70 characters',
  REDDIT_SEGMENT_NAME_MIN_LENGTH_ERROR = 'Segment name must be at least 3 characters',
  REDDIT_SEGMENT_NAME_LENGTH_ERROR = 'Segment name must not exceed 500 characters',
  META_SEGMENT_NAME_LENGTH_ERROR = 'Segment name must not exceed 121 characters',
  META_DESCRIPTION_LENGTH_ERROR = 'Description must not exceed 100 characters',
  TIKTOK_ADVERTISER_ID_REQUIRED_ERROR = 'Advertiser ID is required',
  LIVERAMP_CPM_MIN_ERROR = 'Value must be greater than zero',
  LIVERAMP_PERCENTAGE_OF_MEDIA_MIN_ERROR = 'Value must be greater than zero',
  LIVERAMP_ADVERTISER_DIRECT_MIN_ERROR = 'Value must be greater than zero',
  AMAZON_CPM_INVALID = 'CPM must be greater than 0',
  APPSFLYER_SEGMENT_NAME_LENGTH_ERROR = 'Segment name must not exceed 200 characters',
  APPSFLYER_SEGMENT_NAME_INVALID = 'Segment name may only contain letters (A-Z, a-z), numbers (0-9), and hyphens (-)',
  APPSFLYER_SEGMENT_NAME_START_INVALID = 'Segment name must not start with a number or hyphen',
  APPSFLYER_COLLABORATOR_ID_REQUIRED = 'Collaborator ID is required',
  VIANT_SEGMENT_NAME_ATTAIN_PREFIX = 'Segment name must not start with "Attain >"',
  VIANT_ADVERTISER_ID_REQUIRED = 'Advertiser ID is required',
  VIANT_ADVERTISER_ID_POSITIVE_ERROR = 'Advertiser ID must be a positive integer',
  VIANT_ACCOUNT_ID_REQUIRED = 'Account ID is required',
  VIANT_ACCOUNT_ID_POSITIVE_ERROR = 'Account ID must be a positive integer',
  TTD_ID_ALPHANUMERIC_ERROR = 'Partner ID and Advertiser ID must contain only alphanumeric characters (no spaces or special characters)',
  NEXXEN_SEGMENT_START_ERROR = 'Segment name should start with "Attain >"',
  NEXXEN_SEGMENT_PART_LENGTH_ERROR = 'Each part of the segment name must not exceed 50 characters',
  NEXXEN_ADVERTISER_ID_REQUIRED_ERROR = 'Advertiser ID is required when availability is Single Advertiser',
  NEXXEN_ADVERTISER_ID_INTEGER_ERROR = 'Advertiser ID must be -1 or a non-negative number',
  NEXXEN_MARKET_ID_REQUIRED_ERROR = 'Market ID is required when availability is Single Advertiser',
  NEXXEN_MARKET_ID_INTEGER_ERROR = 'Market ID must be -1 or a non-negative number',
  NEXXEN_CURRENCY_RATE_REQUIRED_ERROR = 'Currency Rate is required',
  NEXXEN_CURRENCY_RATE_POSITIVE_ERROR = 'Currency Rate must be greater than 0',
  NEXXEN_CURRENCY_RATE_MAX_ERROR = 'Currency Rate must not be greater than 30',
  NEXXEN_START_DATE_REQUIRED_ERROR = 'Start Date is required',
  NEXXEN_END_DATE_INVALID_ERROR = 'End Date must be greater than or equal to Start Date',
}

export const DefaultPaginationValues: Pagination = { page: 1, limit: 15 };

export enum ExportAudience {
  KLOVER_IDS = 'Klover IDs',
  KLOVER_RAMP_IDS = 'Klover + Ramp IDs',
  TRANSUNION = 'TransUnion',
  TRANSUNION_TTD = 'TransUnion + TTD',
  TRANSUNION_HEMS = 'TransUnion HEMs Only',
  CUSTOM_SEGMENT = 'Trade Desk Custom Segment',
  OTS_SEGMENT = 'LiveRamp + Trade Desk OTS Segment',
  LIVERAMP_CUSTOM_SEGMENT = 'LiveRamp Custom Segment',
  LIVERAMP_OTS_SEGMENT = 'LiveRamp OTS Segment',
  TTD_DATA_ALLIANCE = 'Trade Desk Data Alliance',
  EXPERIAN_OTS_SEGMENT = 'Experian OTS Segment',
}

export const CUSTOM_SEGMENT_INFORMATION = [
  'ADS audience will include email, phone, first, last name, and address',
  'Sends this audience to TransUnion for lookalike modeling',
  'You need to login to TU to adjust modeling settings',
  'Once TransUnion is finished building the lookalike model, a new custom segment will be created in The Trade Desk.',
];

export const OTS_SEGMENT_INFORMATION = [
  'ADS audience will include HEMs only',
  'Sends this audience to TransUnion for lookalike modeling',
  'TransUnion will automatically create lookalike models and send them to LiveRamp and to our platform',
  'Our platform will create a new OTS segment in The Trade Desk.',
];

export const TTD_DATA_ALLIANCE_SEGMENT_INFORMATION = [
  'Re-uses a previously modeled TransUnion lookalike file, which was originally generated by a previous TTD OTS export.',
  'Our platform will create a new Data Alliance segment in The Trade Desk.',
];

export const EXPORT_EMAIL_INFO_TEXT =
  'Supply an email address to send a CSV file with the audience information you created.';
export const SELECT_EXPORT_TYPE = 'Select export type';
export const REQUIRED_FIELDS_ARE_MISSING = 'Required fields are missing';

export enum AUDIENCE_EXPORT_TYPES {
  RAMP_ID = 'rampID',
  CUSTOM_SEGMENT = 'custom_segment',
  OTS_SEGMENT = 'ots_segment',
  KLOVER_IDS = 'klover_ids',
  TRANSUNION = 'transunion',
  TRANSUNION_TTD = 'transunion_ttd',
  TRANSUNION_HEMS = 'transunion_hems',
  LIVERAMP_CUSTOM_SEGMENT = 'liveramp_custom_segment',
  LIVERAMP_OTS_SEGMENT = 'liveramp_ots_segment',
  PUBMATIC_OTS_SEGMENT = 'pubmatic_ots_segment',
  PUBMATIC_CUSTOM_SEGMENT = 'pubmatic_custom_segment',
  OPENX_OTS_SEGMENT = 'openx_ots_segment',
  OPENX_CUSTOM_SEGMENT = 'openx_custom_segment',
  TTD_DATA_ALLIANCE = 'ttd_data_alliance',
  EXPERIAN_OTS_SEGMENT = 'experian_ots_segment',
  META_OTS_SEGMENT = 'meta_ots_segment',
  X_OTS_SEGMENT = 'x_ots_segment',
  YAHOO_DSP_OTS_SEGMENT = 'yahoo_dsp_ots_segment',
  MAGNITE_OTS_SEGMENT = 'magnite_ots_segment',
  META_CUSTOM_SEGMENT = 'meta_custom_segment',
  X_CUSTOM_SEGMENT = 'x_custom_segment',
  YAHOO_DSP_CUSTOM_SEGMENT = 'yahoo_dsp_custom_segment',
  MAGNITE_CUSTOM_SEGMENT = 'magnite_custom_segment',
  TIKTOK_CUSTOM_SEGMENT = 'tiktok_custom_segment',
  TTD_OTS_SEGMENT_SEED_AUDIENCE = 'ttd_ots_segment_seed_audience',
  TTD_CUSTOM_SEGMENT_SEED_AUDIENCE = 'ttd_custom_segment_seed_audience',
  LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE = 'liveramp_ots_segment_seed_audience',
  LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE = 'liveramp_custom_segment_seed_audience',
  META_CUSTOM_SEGMENT_SEED_AUDIENCE = 'meta_custom_segment_seed_audience',
  TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE = 'tiktok_custom_segment_seed_audience',
  REDDIT_CUSTOM_SEGMENT = 'reddit_custom_segment',
  AMAZON_CUSTOM_SEGMENT = 'amazon_custom_segment',
  AMAZON_OTS_SEGMENT = 'amazon_ots_segment',
  APPSFLYER_CUSTOM_SEGMENT = 'appsflyer_custom_segment',
  VIANT_CUSTOM_SEGMENT = 'viant_custom_segment',
  NEXXEN_OTS_SEGMENT = 'nexxen_ots_segment',
  NEXXEN_CUSTOM_SEGMENT = 'nexxen_custom_segment',
  TTD_REPLACE_NON_MODELED = 'ttd_replace_non_modeled',
  TTD_REPLACE_ATTAIN_MODELED = 'ttd_replace_attain_modeled',
  TTD_REPLACE_COMBINED = 'ttd_replace_combined',
}

export enum LIVERAMP_SEGMENT_FIELD_NAME {
  PROGRAMMATIC_PERCENT_OF_MEDIA = 'Programmatic % of media',
  CPM = 'CPM',
  DESCRIPTION = 'Description',
  CUSTOM_SEGMENT_NAME = 'Custom Segment Name',
  TV_TARGETING = 'TV Targeting CPM',
  ADVERTISER_DIRECT = 'Advertiser Direct CPM',
  CHOOSE_INTEGRATION = 'Choose Integration (Destination)',
  CHOOSE_ADVERTISER = 'Choose Advertiser (Distribution Manager)',
}

export enum QUESTION_FIELD_MAPPING {
  audience_id = 'Audience ID',
  answer_type = 'Answer Type',
  answers = 'Answers',
  points = 'Points',
  question_text = 'Question Text',
  status = 'Status',
  audience_type = 'Audience Type',
}

export enum AUDIENCE_MANAGER_FIELD_MAPPING {
  audience_name = 'Audience Name',
  dimension_groups = 'Dimension Groups',
  dimensions = 'Dimensions',
  size = 'Size',
}

export enum SYSTEM_NAME {
  audience_studio = 'audience_studio',
  audience_manager = 'audience_manager',
  quick_questions_manager = 'quick_questions_manager',
}

export const CHANGE_LOG_ARRAY_FIELDS = [
  'successurl',
  'tune_goals',
  'targeting_states',
  'targeting_zip_codes',
  'answers',
  'dimension_groups',
  'impression_event',
  'dimensions',
  'category',
  'placement',
];

export const CHANGE_LOG_BOOLEAN_FIELDS = [
  'multiple_conversion_event_override',
  'is_disclosure',
  'is_featured',
  'is_new',
  'is_points_change_scheduled',
];

export const CHANGE_LOG_TIMESTAMP_FIELDS = [
  'flight_start_date',
  'flight_end_date',
];

export const ACTIVE_AUDIENCE = 'Active Audience';
export const SELECT_YOUR_DESTINATION = 'Select your destinations';
export const ADJUST_AND_MONITOR_AUDIENCE = 'Adjust & monitor your audience';

export const TRADE_DESK_SEGMENT_DETAILS = 'The Trade Desk Segment Details';
export const LIVERAMP_SEGMENT_DETAILS = 'LiveRamp Segment Details';
export const AUDIENCE_STATUS_HEADER_TITLE = 'Status';
export const AUDIENCE_SELECT_EMAIL_AUTO_COMPLETE = 'Select email';
export const EXPORT_STATUS_HEADER_TITLE = 'What does this mean?';
export const TABLE_SORT_TOOLTIP_TEXT = 'Toggle SortBy';
export const MAX_SEGMENT_NAME_LENGTH = 256;
export const MAX_CHILD_SEGMENT_NAME_LENGTH = 256;
export const EXPERIAN_MAX_CHILD_SEGMENT_NAME_LENGTH = 100;
export const MAX_SEGMENT_DESCRIPTION_LENGTH = 255;
export const OPENX_MAX_SEGMENT_NAME_LENGTH = 100;
export const TTD_DATA_ALLIANCE_MAX_SEGMENT_NAME_LENGTH = 1000;
export const YAHOO_DSP_MAX_SEGMENT_NAME_LENGTH = 255;
export const LIVERAMP_MAX_SEGMENT_NAME_LENGTH = 255;
export const YAHOO_DSP_CPM_MAX_VALUE = 50;
export const YAHOO_DSP_TV_CPM_MAX_VALUE = 50;
export const TIKTOK_MAX_SEGMENT_NAME_LENGTH = 70;
export const REDDIT_MIN_SEGMENT_NAME_LENGTH = 3;
export const REDDIT_MAX_SEGMENT_NAME_LENGTH = 500;
export const META_MAX_SEGMENT_NAME_LENGTH = 121;
export const META_MAX_DESCRIPTION_LENGTH = 100;
export const AUDIENCE_EST_SIZE_TOOLTIP_HEADER = 'Audience Size History';
export const LIVERAMP_SEGMENT_PRICING_DETAILS = 'Pricing Details';
export const LIVERAMP_SEGMENT_ADVERTISER_SELECTION = 'Advertiser Selection';
export const REFRESH_AUDIENCE_DISABLE_MESSAGE =
  'Save audience before refreshing';
export const REFRESH_AUDIENCE = 'Get Latest Audience Size';
export const ATTAIN = 'attain';
export const COMPLETED = 'COMPLETED';
export const EXPORT_MODELED_AUDIENCES = 'Export Modeled Audiences';
export const EXPORT_SEED_AUDIENCES = 'Export Seed Audiences';

/**
 * Audience model size threshold (min audience size to model)
 */
export const AUDIENCE_MODEL_SIZE_THRESHOLD = 1000;
/**
 * Audience model size threshold warning
 */
export const AUDIENCE_MODEL_SIZE_THRESHOLD_WARNING = 2000;

export const doesNotUseIs: StringToBoolMap = {
  balance: true,
  age: true,
  numberOfKids: true,
  numberInHousehold: true,
};

export const AudienceEstSizeLabel = {
  EXPORTED_AUDIENCE: 'Exported Audience',
  SEED_AUDIENCE: 'Seed Audience',
  TTD_UUID2: 'TTD UID2s',
  TTD_AUDIENCE: 'TTD Audience',
};

export const AudienceSizeHistoryLabel = {
  SEED_AUDIENCE: 'Seed Audience',
  EXPORTED_AUDIENCE: 'Exported Audience',
  UUID: 'UUID',
  TREAD_DESK: 'Trade Desk',
  ACTIVE_IDS: 'Active IDs (a count of matched users over the last 7D)',
  RECEIVED_IDS: 'Received IDs (expires after 30D)',
};

export const AudienceTypeLabel = {
  HEM_SHA256: {
    PRIMARY_TEXT: 'Emails',
    SECONDARY_TEXT: '(Trade Desk, OpenX)',
  },
  HEM_SHA256_HEX: {
    PRIMARY_TEXT: 'Emails',
    SECONDARY_TEXT: '(Trade Desk, OpenX)',
  },
  IP: {
    PRIMARY_TEXT: 'IPs',
    SECONDARY_TEXT: '(Pubmatic)',
  },
  LUID: {
    PRIMARY_TEXT: 'LUIDs',
    SECONDARY_TEXT: '(Experian)',
  },
};

export const CombinedAudiencePlatformLabel = [
  {
    PRIMARY_TEXT: 'Trade Desk',
    SECONDARY_TEXT: '(UUID)',
    TYPE: 'HEM_SHA256_HEX',
  },
  {
    PRIMARY_TEXT: 'LiveRamp',
    SECONDARY_TEXT: '(Email)',
    TYPE: 'HEM_SHA256_HEX',
  },
  {
    PRIMARY_TEXT: 'Yahoo',
    SECONDARY_TEXT: '(Email)',
    TYPE: 'HEM_SHA256',
  },
  {
    PRIMARY_TEXT: 'Meta',
    SECONDARY_TEXT: '(Email)',
    TYPE: 'HEM_SHA256',
  },
  {
    PRIMARY_TEXT: 'Reddit',
    SECONDARY_TEXT: '(Email)',
    TYPE: 'HEM_SHA256',
  },
  {
    PRIMARY_TEXT: 'OpenX',
    SECONDARY_TEXT: '(Email)',
    TYPE: 'HEM_SHA256',
  },
];

export const AudiencePlatformLabel = [
  {
    PRIMARY_TEXT: 'Trade Desk',
    SECONDARY_TEXT: '(UUID)',
    TYPE: 'HEM_SHA256',
  },
  {
    PRIMARY_TEXT: 'LiveRamp',
    SECONDARY_TEXT: '(Email)',
    TYPE: 'HEM_SHA256',
  },
  {
    PRIMARY_TEXT: 'Yahoo',
    SECONDARY_TEXT: '(Email)',
    TYPE: 'HEM_SHA256',
  },
  {
    PRIMARY_TEXT: 'Meta',
    SECONDARY_TEXT: '(Email)',
    TYPE: 'HEM_SHA256',
  },
  {
    PRIMARY_TEXT: 'Reddit',
    SECONDARY_TEXT: '(Email)',
    TYPE: 'HEM_SHA256',
  },
  {
    PRIMARY_TEXT: 'OpenX',
    SECONDARY_TEXT: '(Email)',
    TYPE: 'HEM_SHA256',
  },
];

export const OfferFieldValidationMessage = {
  HEADLINE: 'Offer does not have a headline',
  BODY: 'Offer does not have a body',
  DESCRIPTION: 'Offer does not have a description',
  CTA_TEXT: 'Offer does not have a call to action',
  OFFER_NAME: 'Offer does not have an offer name',
  BG_IMAGE_URL: 'Offer does not have a background img url',
  COM_TEXT: 'Offer does not have comparison text',
  SUBLINE: 'Offer does not have a subline',
  FULL_SCREEN_IMAGE_URL: 'Offer does not have a full screen image url',
  CATEGORY: 'Offer does not have a category',
};

/**
 * Exports list used to derive status in Audience Manager/Custom Audience list page
 */
export const AudienceDefaultExports = [
  AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.TRANSUNION,
];

/**
 * Export list used to filter audience details in Audience Manager > Export History List
 */
export const AudienceAllExports = [
  AUDIENCE_EXPORT_TYPES.TRANSUNION,
  AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.TTD_DATA_ALLIANCE,
  AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.OPENX_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.OPENX_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.X_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.X_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.META_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.MAGNITE_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.MAGNITE_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.REDDIT_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.AMAZON_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.APPSFLYER_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.TTD_REPLACE_NON_MODELED,
  AUDIENCE_EXPORT_TYPES.TTD_REPLACE_ATTAIN_MODELED,
  AUDIENCE_EXPORT_TYPES.TTD_REPLACE_COMBINED,
];

/**
 * Export List for which segment details will be visible in Audience Manager/Custom Audience > Export History List
 */
export const AllowSegmentDetailsExports = [
  AUDIENCE_EXPORT_TYPES.KLOVER_IDS,
  AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.TTD_DATA_ALLIANCE,
  AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.OPENX_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.OPENX_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.X_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.X_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.META_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.MAGNITE_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.MAGNITE_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE,
  AUDIENCE_EXPORT_TYPES.REDDIT_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.AMAZON_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.APPSFLYER_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.NEXXEN_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.NEXXEN_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.TTD_REPLACE_NON_MODELED,
  AUDIENCE_EXPORT_TYPES.TTD_REPLACE_ATTAIN_MODELED,
  AUDIENCE_EXPORT_TYPES.TTD_REPLACE_COMBINED,
];

/**
 * Export list used to filter audience details in Custom Audience > Export History List
 */
export const AllowCustomAudiencePartnerExports = [
  AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.TRANSUNION,
  AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT,
  AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT,
  AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT,
];

/**
 * List of all System Used filters in Audience Manager list page
 */
export const AUDIENCE_TYPE_LABLES = {
  DESIGNER: 'Designer',
  BLENDER: 'Blender',
  FILE: 'File',
};

/**
 * List of all platforms filters in Audience Manager list page
 */
export const PLATFORMS_LABLES = {
  NOT_EXPORTED: 'Not Exported',
  KLOVER_IDS: 'Klover IDs',
  TTD: 'Trade Desk',
  TRANSUNION: 'Transunion',
  LIVERAMP: 'Liveramp',
  OPENX: 'OpenX',
  PUBMATIC: 'Pubmatic',
  EXPERIAN: 'Experian',
  META: 'Meta',
  X: 'X',
  MAGNITE: 'Magnite',
  YAHOO_DSP: 'Yahoo DSP',
  TIKTOK: 'TikTok',
  REDDIT: 'Reddit',
  AMAZON: 'Amazon',
  APPSFLYER: 'AppsFlyer',
  VIANT: 'Viant',
  NEXXEN: 'Nexxen',
};

/**
 * List of all platforms keys
 */
export const PLATFORMS_KEYS = {
  NOT_EXPORTED: 'not_exported',
  KLOVER_IDS_KEY: 'klover_ids',
  TTD_KEY: 'trade_desk',
  TRANSUNION_KEY: 'transunion',
  LIVERAMP_KEY: 'liveramp',
  OPENX_KEY: 'openx',
  PUBMATIC_KEY: 'pubmatic',
  EXPERIAN_KEY: 'experian',
  META_KEY: 'meta',
  X_KEY: 'x',
  MAGNITE_KEY: 'magnite',
  YAHOO_DSP_KEY: 'yahoo_dsp',
  TIKTOK_KEY: 'tiktok',
  REDDIT_KEY: 'reddit',
  AMAZON_KEY: 'amazon',
  APPSFLYER_KEY: 'appsflyer',
  VIANT_KEY: 'viant',
  NEXXEN_KEY: 'nexxen',
};

/**
 * Audience lal modal type while creating audience
 */
export const AUDIENCE_LAL_MODAL_TYPE = {
  TRANSUNION: 'transunion',
  ATTAIN: 'attain',
};

/**
 * Audience export lal modal type
 */
export const AUDIENCE_EXPORT_LAL_MODAL_TYPE = {
  ATTAIN: 'attain',
  META: 'meta',
  TIKTOK: 'tiktok',
};

/** API values for audience export `modelScoringType` (must align with ads-server). */
export const MODEL_SCORING_TYPE = {
  LAL: 'lal',
  CPM_V1: 'cpm_v1',
} as const;

export const MODEL_SCORING_TYPE_MAPPING = {
  [MODEL_SCORING_TYPE.LAL]: 'LAL',
  [MODEL_SCORING_TYPE.CPM_V1]: 'CPM_V1',
} as const;

export type ModelScoringTypeValue =
  (typeof MODEL_SCORING_TYPE)[keyof typeof MODEL_SCORING_TYPE];

export const MODEL_SCORING_TYPE_OPTIONS: ReadonlyArray<{
  label: string;
  value: ModelScoringTypeValue;
}> = [
  { label: 'LAL', value: MODEL_SCORING_TYPE.LAL },
  { label: 'CPM_V1', value: MODEL_SCORING_TYPE.CPM_V1 },
];

/** UI tab order for model scoring mode (Lookalike first). */
export const MODEL_SCORING_MODE_TAB_ORDER: readonly ModelScoringTypeValue[] = [
  MODEL_SCORING_TYPE.LAL,
  MODEL_SCORING_TYPE.CPM_V1,
];

/**
 * Enum for systemUsed column mapping
 */
export const ENUM_SYSTEM_USED = {
  STANDARD_AUDIENCE: 'standard_audience',
  CUSTOM_AUDIENCE: 'custom_audience',
  COMBINED_AUDIENCE: 'combined_audience',
};

export const ENUM_SYSTEM_USED_LABLE = {
  standard_audience: AUDIENCE_TYPE_LABLES.DESIGNER,
  combined_audience: AUDIENCE_TYPE_LABLES.BLENDER,
  custom_audience: AUDIENCE_TYPE_LABLES.FILE,
};

export const CREATE_AUDIENCE_TYPE = {
  BUILD_FROM_SCRATCH: 'build_from_scratch',
  COMBINE_EXISTING: 'combine_existing',
};

export const OPERATOR_TYPE = {
  OR: 'OR',
  AND: 'AND',
};

export const AUDIENCE_SIZE_ID_TYPES = {
  HEM_SHA256_HEX: 'HEM_SHA256_HEX',
  LUID: 'LUID',
  IP: 'IP',
};

export const GENERATION_STATUS = {
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
};

export const COMBINED_AUDIENCE_GENERATION = 'combined_audience_generation';
export const CREATION_APPLICATION = {
  KDS: 'KDS',
  ADS: 'ADS',
  OHQ: 'OHQ',
};

export const OFFER_STATE_PROPS = {
  isTuneGoalIdValidating: 'isTuneGoalIdValidating',
};

/**
 * List of all timezone options
 */
export const TIMEZONE_OPTIONS = [{ label: 'ET', value: 'et' }];

export const TIMEZONE_OPTIONS_MAP = {
  et: 'America/New_York',
};

/**
 * Pusher notification status
 */
export const PUSHER_NOTIFICATION_STATUS = {
  DONE: 'done',
  ERROR: 'error',
};

/**
 * Audience compliance status
 */
export const AUDIENCE_COMPLIANCE_STATUS = {
  ALLOWED: 'ALLOWED',
  FULL_BAN: 'FULL_BAN',
  PARTIAL_BAN: 'PARTIAL_BAN',
  UNKNOWN: 'UNKNOWN',
};

/**
 * Keywords to suppress in console logs
 */
export const LOG_SUPPRESSION_KEYWORDS = ['LaunchDarkly', 'Apollo DevTools'];

/**
 * Multiplier for meta model cut size
 */
export const META_MODEL_CUT_SIZE_MULTIPLIER = 2.7;
export const META_AUDIENCE_SIZE_EXPORT_THRESHOLD = 300;
export const TIKTOK_AUDIENCE_SIZE_EXPORT_THRESHOLD = 1000;

export const EXPORT_TYPES_FIELDS_REQUIRED = {
  cpm: [
    AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.AMAZON_OTS_SEGMENT,
  ],
  percentageOfMedia: [
    AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE,
  ],
  description: [
    AUDIENCE_EXPORT_TYPES.OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.PUBMATIC_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.PUBMATIC_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.EXPERIAN_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.TTD_OTS_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.TTD_CUSTOM_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_OTS_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.LIVERAMP_CUSTOM_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.META_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.TIKTOK_CUSTOM_SEGMENT_SEED_AUDIENCE,
    AUDIENCE_EXPORT_TYPES.YAHOO_DSP_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.YAHOO_DSP_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.REDDIT_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.VIANT_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.AMAZON_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.AMAZON_OTS_SEGMENT,
  ],
  default: [
    //For below export types cpm, percentageOfMedia, description are visible
    AUDIENCE_EXPORT_TYPES.X_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.X_CUSTOM_SEGMENT,
    AUDIENCE_EXPORT_TYPES.META_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.MAGNITE_OTS_SEGMENT,
    AUDIENCE_EXPORT_TYPES.MAGNITE_CUSTOM_SEGMENT,
  ],
};
